import Bowser from 'bowser';
import { computed, ref } from 'vue';

// Screen

export const isMobile = ref<boolean>();
export const isTablet = ref<boolean>();
export const isLargeTablet = ref<boolean>();
export const isLaptop = ref<boolean>();
export const isDesktop = ref<boolean>();
export const isHuge = ref<boolean>();
export const breakpoints = { isMobile, isTablet, isLargeTablet, isLaptop, isDesktop, isHuge };

// Device & client

export const isPWA = ref<boolean>(false);
export const isSafari = ref<boolean>(false);
export const isIOS = ref<boolean>(false);
export const isAndroid = ref<boolean>(false);

export const resizeCount = ref(0);
export const width = ref(window.innerWidth);
export const height = ref(window.innerHeight);

export const isVerticallyCondensed = computed(() => height.value < 630);

// TODO: Centralize this to navigationStore
export const isNavCollapsed = computed(() => isTablet.value || isLargeTablet.value || isDesktop.value);

// Responsive media

const queries = {
  isMobile: '(max-width: 975px)',
  isTablet: '((min-width: 976px) and (max-width: 1023px))',
  isLargeTablet: '((min-width: 1024px) and (max-width: 1179px))',
  isLaptop: '((min-width: 1180px) and (max-width: 1279px))',
  isDesktop: '((min-width: 1280px) and (max-width: 1439x))',
  isHuge: '(min-width: 1440px)',
};

function determineMedia() {
  isMobile.value = window.matchMedia(queries.isMobile).matches;
  isTablet.value = window.matchMedia(queries.isTablet).matches;
  isLargeTablet.value = window.matchMedia(queries.isLargeTablet).matches;
  isLaptop.value = window.matchMedia(queries.isLaptop).matches;
  isDesktop.value = window.matchMedia(queries.isDesktop).matches;
  isHuge.value = window.matchMedia(queries.isHuge).matches;

  resizeCount.value = resizeCount.value + 1;
  width.value = window.innerWidth;
  height.value = window.innerHeight;
}

determineMedia();

window.addEventListener('resize', determineMedia);

// Safari

function detectSafari(): boolean {
  try {
    const clientInfo = Bowser.parse(window.navigator.userAgent);
    const { browser } = clientInfo || {};
    const { name } = browser || {};

    if (name?.toLowerCase() === 'safari') {
      const bodyEl = document.querySelector('body');
      bodyEl?.classList.add('safari-quirks');

      return true;
    }
  } catch (e) {
    throw new Error(`Couldnt detect client browser: ${e}`);
  }

  return false;
}

isSafari.value = detectSafari();

// iOS

function detectIOS() {
  const parser = Bowser.getParser(window.navigator.userAgent);
  const os = parser.getOS();
  return os.name === 'iOS';
}

isIOS.value = detectIOS();

// iPadOS (reports as macOS in user agent since iPadOS 13+)

function detectIPadOS(): boolean {
  const parser = Bowser.getParser(window.navigator.userAgent);
  const os = parser.getOS();
  return os.name === 'macOS' && navigator.maxTouchPoints > 1;
}

export const isIPadOS = ref<boolean>(detectIPadOS());

// macOS Safari (Add to Dock arrived in Safari 17; there is no install prompt to call)

function detectMacSafari(): boolean {
  if (!isSafari.value || isIPadOS.value) return false;
  return Bowser.getParser(window.navigator.userAgent).satisfies({ macos: { safari: '>=17' } }) === true;
}

export const isMacSafari = ref<boolean>(detectMacSafari());

// Android

function detectAndroid(): boolean {
  const parser = Bowser.getParser(window.navigator.userAgent);
  const os = parser.getOS();
  return os.name === 'Android';
}

isAndroid.value = detectAndroid();

// PWA

function detectPWA(): boolean {
  const isPWA =
    window.matchMedia('(display-mode: standalone)').matches ||
    // @ts-expect-error: An Android-only property
    window.navigator?.standalone === true ||
    document.referrer.startsWith('android-app://');
  return isPWA;
}

isPWA.value = detectPWA();

// Notifications supported

export const isMobileOS = computed(() => isIOS.value || isAndroid.value || isIPadOS.value);

export const areNotificationsSupported = computed(() => {
  return !isMobileOS.value || (isMobileOS.value && isPWA.value);
});

/* Phone PWAs only: Sparks / Walk / Squares are portrait layouts. iPad keeps
   rotation — enough space, few users, and a manifest lock would close it too.
   Mobile browser tabs cannot be locked; failures here are expected and ignored. */
export function lockPhonePwaPortrait(): void {
  if (!isPWA.value || isIPadOS.value) return;
  if (!isIOS.value && !isAndroid.value) return;

  // `lock` is omitted from TypeScript's ScreenOrientation (not universal); the
  // runtime check below is the real gate.
  const orientation = window.screen?.orientation as ScreenOrientation & {
    lock?: (orientation: string) => Promise<void>;
  };
  if (typeof orientation?.lock !== 'function') return;

  void orientation.lock('portrait').catch(() => undefined);
}
